import {
  HERO_VIDEO_MAX_BYTES,
  HERO_VIDEO_MAX_SECONDS,
  HERO_VIDEO_MAX_WIDTH,
  HERO_VIDEO_TYPE,
} from "../events/landing";
import { readVideoMetadata } from "./video-metadata";

const MIB = 1024 * 1024;

/**
 * Why a file cannot be the hero video, or null when it can.
 *
 * Type and size are what storage.rules enforce, checked first and without
 * reading the file. Duration and width the rules cannot see, so they are read
 * from the file in the browser before anything is uploaded. Each answer says
 * what the file is and what the limit is, because "too big" does not tell an
 * organiser what to change. Sizes are in MiB, which is what the 10 MiB rule is,
 * and a length is rounded up so a clip just over never reads as within.
 *
 * Rejects only when `signal` aborts, so a caller that went away can stop.
 */
export async function heroVideoProblem(
  file: File,
  signal?: AbortSignal,
): Promise<string | null> {
  if (file.type !== HERO_VIDEO_TYPE)
    return "Choose an MP4 video. A .mov or .webm file has to be converted first.";
  if (file.size > HERO_VIDEO_MAX_BYTES)
    return `That video is ${(file.size / MIB).toFixed(1)} MiB. The limit is ${HERO_VIDEO_MAX_BYTES / MIB} MiB.`;

  const unreadable =
    "This browser could not read that video. Convert it to an H.264 MP4 with the command below.";
  const tooSlow =
    "This browser took too long to read that video. Convert it to an H.264 MP4 with the command below.";
  let duration: number;
  let width: number;
  try {
    ({ duration, width } = await readVideoMetadata(file, signal));
  } catch (cause) {
    const name = cause instanceof Error ? cause.name : "";
    // The caller gave up; the file is not to blame, so there is nothing to say.
    if (name === "AbortError") throw cause;
    return name === "TimeoutError" ? tooSlow : unreadable;
  }
  // A stream reports Infinity and a broken header NaN; neither is a length.
  // Zero is what a browser reports for a file it opened but cannot decode:
  // an audio-only MP4 has no picture, and HEVC without a decoder has no size.
  if (
    !Number.isFinite(duration) ||
    !Number.isFinite(width) ||
    duration <= 0 ||
    width <= 0
  )
    return unreadable;
  if (duration > HERO_VIDEO_MAX_SECONDS)
    return `That video is ${Math.ceil(duration)} seconds long. The limit is ${HERO_VIDEO_MAX_SECONDS} seconds.`;
  if (width > HERO_VIDEO_MAX_WIDTH)
    return `That video is ${width} pixels wide. The limit is ${HERO_VIDEO_MAX_WIDTH} pixels.`;
  return null;
}
